import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AthleteModeProvider, useAthleteMode } from "./AthleteModeContext";
import { logout } from "../../../store/actions/userActions";
import { fetchAthleteProfileByEmail } from "../../../store/actions/athleteActions";
import wordmark from "../../../assets/brand/cache-wordmark.svg";
import iconMail from "../../../assets/brand/icon-mail.svg";
import { initialsOf } from "../../ForBrands/brandDealUtils";
import "../../ForBrands/brandMode.css";
import { useFitScale, fitScaleStyle } from "../../ForBrands/useFitScale";
import { PhoneThemeProvider, usePhoneTheme } from "../../ForBrands/useIsPhone";
import { MobileBottomNav, MobileTopBar, NAV_ICONS } from "../../ForBrands/ui/mobile/MobileShell";
import { ATHLETE_LANDING } from "../../../constants";
import { isPremium, planLine, usePlan } from "../../common/PlanBadge";

// Athlete Mode shell (Figma "AppShell / Athlete"): the same 72px topbar and
// tab bar as Brand Mode, with the ATHLETE MODE label and the My Profile /
// Dashboard / My Circle / My Deals tabs (Atlas is hidden for now). Profile
// comes first and Home reads Dashboard (owner review, 9/29); the routes stay
// /athlete/profile and /athlete/home. Log in and the wordmark open the first
// tab, My Profile (ATHLETE_LANDING). The global Navbar and Sidebar are
// hidden on /athlete routes so this is the only chrome.
//
// Below 640px (the phone theme, Figma ATHLETE MODE V2 mobile) the shell
// swaps that chrome for one of two kinds, by route:
//  - tabs: the 55px MobileTopBar (2000:306) on top and the fixed
//    MobileBottomNav (2000:333) at the bottom;
//  - stack: no shell chrome at all. The deal flow, messages and the edit
//    hub are pushed screens that render their own MobileStackHeader.
// 640px and up keeps the desktop header, tab row and main exactly as they
// were.

const TABS = [
  { path: "/athlete/profile", label: "My Profile", match: ["/athlete/profile"] },
  { path: "/athlete/home", label: "Dashboard", match: ["/athlete/home"] },
  { path: "/athlete/circle", label: "My Circle", match: ["/athlete/circle"] },
  // Atlas is hidden while the feature is still being built; uncomment to show the tab again.
  // { path: "/athlete/atlas", label: "Atlas", match: ["/athlete/atlas"], badge: "atlasMatches" },
  { path: "/athlete/deals", label: "My Deals", match: ["/athlete/deals", "/athlete/messages"], badge: "pendingDeals" },
];

// Phone routes that are pushed screens (no top bar, no bottom nav): the edit
// hub, every deal screen under /athlete/deals/:dealId (tracking, offer,
// counter, contract) and the messages inbox and thread. Everything else,
// billing and password included, gets the tabs chrome.
const PHONE_STACK = [/^\/athlete\/profile\/edit(\/|$)/, /^\/athlete\/deals\/[^/]+/, /^\/athlete\/messages(\/|$)/];

// The bottom nav keeps the Figma short labels, with Profile first and Home
// read as Dashboard (desktop: My Profile, Dashboard, My Circle, My Deals).
// Atlas is hidden in both while the feature is being built; its route still
// opens by URL.
const PHONE_TABS = [
  { key: "profile", to: "/athlete/profile", label: "Profile", icon: NAV_ICONS.profile, match: ["/athlete/profile"] },
  { key: "home", to: "/athlete/home", label: "Dashboard", icon: NAV_ICONS.home, match: ["/athlete/home"] },
  // Atlas is hidden while the feature is still being built; uncomment to show the tab again.
  // { key: "atlas", to: "/athlete/atlas", label: "Atlas", icon: NAV_ICONS.atlas, match: ["/athlete/atlas"], badge: "atlasMatches" },
  {
    key: "deals",
    to: "/athlete/deals",
    label: "Deals",
    icon: NAV_ICONS.deals,
    match: ["/athlete/deals", "/athlete/messages"],
    badge: "pendingDeals",
  },
  { key: "circle", to: "/athlete/circle", label: "Circle", icon: NAV_ICONS.circle, match: ["/athlete/circle"] },
];

// Phone `main` per chrome: tab pages get the 24px gutter, the 20px top and
// room for the 67px bottom nav plus its 32px breathing space and the safe
// area; pushed screens own their full-bleed strips and add px-6 themselves.
const PHONE_MAIN = {
  tabs: "w-full px-6 pt-5 pb-[calc(99px+env(safe-area-inset-bottom))]",
  stack: "w-full",
};

const DESKTOP_MAIN = "w-full px-5 pb-[70px] sm:px-8 lg:px-12 min-[1440px]:px-[122px]";

const phoneChromeFor = (pathname) => (PHONE_STACK.some((re) => re.test(pathname || "")) ? "stack" : "tabs");

const displayName = (user, athlete) => {
  const first = athlete?.name || user?.firstName || (user?.name || "").split(" ")[0] || "";
  const last = athlete?.surname || user?.lastName || user?.surname || ((user?.name || "").split(" ").slice(1).join(" ") || "");
  const full = `${first} ${last}`.trim();
  return full || user?.email || "Athlete";
};

// `compact` is the phone top bar's version (2000:306): a 30px avatar with
// the name for screen readers only, and the same menu 40px below it. It is
// the only way to Billing, Password, Public profile and Log out on a phone.
// Under the email, the plan ("Athlete Premium · Team plan" when the team's
// coach pays for it, "Athlete Premium", "Free plan") links to Billing; it
// shows once the entitlement has loaded.
export const AvatarMenu = ({ user, athlete, compact = false }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const plan = usePlan();
  const planText = planLine(plan);
  const name = displayName(user, athlete);
  const initials = initialsOf(name.split(" ")[0], name.split(" ").slice(1).join(" "));
  const avatar = athlete?.avatar || user?.avatar;
  // ?preview=1 lets the athlete see their own public card (without it /p/:id
  // sends a signed-in athlete back to My Profile).
  const publicId = athlete?.athleteId || user?.athleteId || "";

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", onClick);
    // iOS Safari sends no mousedown for a tap on plain content, so the
    // phone menu also closes on a touch outside it.
    if (compact) document.addEventListener("touchstart", onClick);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("touchstart", onClick);
    };
  }, [open, compact]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("myEmail");
    localStorage.removeItem("athleteEmail");
    localStorage.removeItem("expiresAt");
    dispatch(logout());
    navigate("/login");
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={compact ? "flex items-center rounded-full" : "flex items-center gap-4"}
      >
        <span className={compact ? "sr-only" : "hidden bm-label-l text-bm-ink sm:inline"}>{name}</span>
        {avatar ? (
          <img src={avatar} alt="" className={`${compact ? "h-[30px] w-[30px]" : "h-8 w-8"} rounded-full object-cover`} />
        ) : (
          <span
            className={`flex ${
              compact ? "h-[30px] w-[30px]" : "h-8 w-8"
            } items-center justify-center rounded-full bg-bm-orange bm-label-xs text-white`}
          >
            {initials}
          </span>
        )}
      </button>
      {open && (
        <div
          role="menu"
          className={`bm-fade-in absolute right-0 ${
            compact ? "top-10" : "top-11"
          } z-40 w-56 overflow-hidden rounded-[12px] border border-bm-border bg-white py-2 shadow-xl`}
        >
          <div className="px-4 pb-2 pt-1">
            <div className="bm-label-l text-bm-ink">{name}</div>
            <div className="truncate bm-body-s text-bm-muted">{user?.email}</div>
            {planText && (
              <Link
                to="/athlete/billing"
                className={`mt-1 block bm-label-xs hover:underline ${isPremium(plan) ? "text-bm-orange" : "text-bm-muted"}`}
              >
                {planText}
              </Link>
            )}
          </div>
          <div className="border-t border-bm-border" />
          <Link to="/athlete/profile/edit" className="block px-4 py-2 bm-body-m text-bm-ink hover:bg-bm-subtle">
            Edit my profile
          </Link>
          {publicId && (
            <Link to={`/p/${publicId}?preview=1`} className="block px-4 py-2 bm-body-m text-bm-ink hover:bg-bm-subtle">
              Public profile
            </Link>
          )}
          <Link to="/athlete/billing" className="block px-4 py-2 bm-body-m text-bm-ink hover:bg-bm-subtle">
            Billing
          </Link>
          <Link to="/athlete/password" className="block px-4 py-2 bm-body-m text-bm-ink hover:bg-bm-subtle">
            Password
          </Link>
          <div className="border-t border-bm-border" />
          <button type="button" onClick={handleLogout} className="block w-full px-4 py-2 text-left bm-body-m text-bm-ink hover:bg-bm-subtle">
            Log out
          </button>
        </div>
      )}
    </div>
  );
};

const Shell = () => {
  const location = useLocation();
  const user = useSelector((state) => state.user.user || {});
  const storedAthlete = useSelector((state) => state.athlete?.data || null);
  // Only the signed-in account's own doc: never show a previous sign-in's
  // name or photo while this account's doc is still loading.
  const myEmail = (localStorage.getItem("myEmail") || "").trim().toLowerCase();
  const athlete =
    storedAthlete && (!storedAthlete.email || String(storedAthlete.email).trim().toLowerCase() === myEmail) ? storedAthlete : null;
  const { badges } = useAthleteMode();
  const dispatch = useDispatch();

  // The avatar menu shows the athlete's photo + name from their athlete
  // doc; load it once for the shell (pages that need it refetch anyway).
  useEffect(() => {
    const email = localStorage.getItem("myEmail");
    if (email) dispatch(fetchAthleteProfileByEmail(email));
  }, [dispatch]);

  // Pages outside the tab set (billing, the edit wizard) highlight nothing.
  const fit = useFitScale();

  const activeTab = TABS.find((t) => t.match.some((m) => location.pathname.startsWith(m))) || null;

  // null on tablets and desktops; "tabs" or "stack" on phones.
  const phone = usePhoneTheme();
  const chrome = phone ? phoneChromeFor(location.pathname) : null;

  // The chrome slots around <main> render nothing when unused, so <main>
  // keeps its place in every layout: crossing 640px (rotating a phone) or
  // moving between a tab page and a pushed screen never remounts the page.
  return (
    <div className="bm-root bg-white" style={fitScaleStyle(fit)}>
      {!chrome && (
        <header className="bg-white">
          <div className="flex h-[72px] w-full items-center justify-between px-5 sm:px-8 lg:px-12 min-[1440px]:px-[122px]">
            <Link to={ATHLETE_LANDING} className="flex items-center" aria-label="Athlete Mode home">
              <img src={wordmark} alt="CACHE" className="h-[19px] w-[95px]" />
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/athlete/messages" className="flex items-center gap-[6px]" aria-label="Messages">
                <img src={iconMail} alt="" className="h-5 w-5" />
                {badges.unread > 0 && (
                  <span className="flex h-[22px] items-center justify-center rounded-full bg-bm-orange px-[9px] bm-label-xs text-white">
                    {badges.unread}
                  </span>
                )}
              </Link>
              <span className="h-5 w-px bg-bm-border" />
              <span className="hidden bm-overline md:inline">Athlete Mode</span>
              <AvatarMenu user={user} athlete={athlete} />
            </div>
          </div>
          <nav className="border-b border-bm-border">
            <div className="flex h-[51px] w-full items-stretch overflow-x-auto px-5 sm:px-8 lg:px-12 min-[1440px]:px-[122px]">
              {TABS.map((tab) => {
                const active = tab === activeTab;
                const count = tab.badge ? badges[tab.badge] : 0;
                return (
                  <NavLink
                    key={tab.path}
                    to={tab.path}
                    className={`flex shrink-0 items-center gap-2 px-6 font-archivo text-[15px] font-bold leading-5 ${
                      active ? "border-b-2 border-bm-orange text-bm-orange" : "text-bm-muted hover:text-bm-ink"
                    }`}
                  >
                    {tab.label}
                    {count > 0 && (
                      <span
                        className={`flex h-[22px] items-center justify-center rounded-full px-[9px] bm-label-xs ${
                          active ? "bg-bm-orange text-white" : "bg-bm-border text-bm-muted"
                        }`}
                      >
                        {count}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </nav>
        </header>
      )}
      {chrome === "tabs" && (
        <MobileTopBar
          homeTo={ATHLETE_LANDING}
          homeLabel="Athlete Mode home"
          mail={{ to: "/athlete/messages", label: "Messages", count: badges.unread }}
          menu={<AvatarMenu user={user} athlete={athlete} compact />}
        />
      )}

      <main className={chrome ? PHONE_MAIN[chrome] : DESKTOP_MAIN}>
        <Outlet />
      </main>
      {chrome === "tabs" && (
        <MobileBottomNav label="Athlete Mode" tabs={PHONE_TABS} pathname={location.pathname} badges={badges} />
      )}
    </div>
  );
};

// PhoneThemeProvider switches the phone theme on for the shell and every
// page and modal inside it (usePhoneTheme is still false at 640px and up).
const AthleteModeLayout = () => (
  <PhoneThemeProvider>
    <AthleteModeProvider>
      <Shell />
    </AthleteModeProvider>
  </PhoneThemeProvider>
);

export default AthleteModeLayout;
